import React from 'react';

export type SalesStatus = 'pay-now' | 'pay-soon' | 'selling' | 'paid' | 'overpaid' | null;

/**
 * Determines the sales status based on start/end dates and payment status.
 * - 'overpaid': paidOut > afEarnings (betalt mer än vad som ska vara)
 * - 'paid': paidOut > 0 AND paidOut >= afEarnings (oavsett försäljningsperiod)
 * - 'pay-now': 7+ days after end date (endast om paidOut === 0)
 * - 'pay-soon': 1-6 days after end date (endast om paidOut === 0)
 * - 'selling': Currently within sales period (endast om paidOut === 0)
 * - null: No active status
 */
export const getSalesStatus = (
  startDate?: string | null,
  endDate?: string | null,
  paidOut?: number,
  afEarnings?: number
): SalesStatus => {
  if (!startDate || !endDate) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);

  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);

  const daysSinceEnd = Math.floor((today.getTime() - end.getTime()) / (1000 * 60 * 60 * 24));

  // Check if overpaid
  if (paidOut !== undefined && afEarnings !== undefined && paidOut > afEarnings) {
    return 'overpaid';
  }

  // Check if paid out: If paidOut > 0 and matches afEarnings, show "Utbetalt" regardless of sales period
  if (paidOut !== undefined && paidOut > 0 && afEarnings !== undefined && paidOut >= afEarnings) {
    return 'paid';
  }

  // Date-based statuses (only used when paidOut === 0 or undefined)
  if (daysSinceEnd >= 7) {
    return 'pay-now';
  } else if (daysSinceEnd >= 1) {
    return 'pay-soon';
  } else if (today >= start && today <= end) {
    return 'selling';
  }

  return null;
};

/**
 * Returns a styled badge component for the given sales status.
 */
export const getSalesStatusBadge = (status: SalesStatus, paidOut?: number, afEarnings?: number): React.ReactNode => {
  if (!status) return null;

  const badges = {
    'pay-now': <span className="sales-status-badge pay-now">Dags att betala ut</span>,
    'pay-soon': <span className="sales-status-badge pay-soon">Snart dags att betala</span>,
    'selling': <span className="sales-status-badge selling">Försäljning pågår</span>,
    'paid': <span className="sales-status-badge paid">Utbetalt</span>,
    'overpaid': <span className="sales-status-badge overpaid">Utbetalt +{((paidOut || 0) - (afEarnings || 0))} kr</span>
  };

  return badges[status];
};

